import React from "react";
import "./index.scss";
import classNames from "classnames";

export interface TabBarNav {
  label: string;
  className?: string;
  // Phase 42: renders nothing at 0 or absent (see the truthiness check below).
  badge?: number;
  onChangeActiveTab: (label: string) => void;
}

const TabBarNav: React.FC<TabBarNav> = (props) => {
  const { label, className, badge, onChangeActiveTab } = props;
  const classes = classNames("tab-nav-item", className);
  const hasBadge = Boolean(badge);
  // The count is an unbounded org-wide total with no natural cap, so only the DISPLAYED digits are
  // clamped -- the `badge` prop itself stays the real number. A 3-4 digit backlog would otherwise
  // widen the pill, and the tab button around it (which has no max-width of its own), far enough to
  // wrap the flex row.
  const badgeText = typeof badge === "number" && badge > 99 ? "99+" : badge;

  return (
    <button
      className={classes}
      onClick={() => onChangeActiveTab(label)}
      // The badge span below is aria-hidden, keeping its raw digits out of the button's default
      // accessible-name computation -- without this label that name would read "Review 3" and change
      // on every poll. Set only when there IS a count, so every badge-less consumer keeps its plain
      // label as the accessible name, unchanged.
      aria-label={hasBadge ? `${label} (${badge} pending)` : undefined}
    >
      {/* The label lives in its own element, the badge as a sibling -- load-bearing for the DOM-shape
          assertion in TabBar.test.tsx (plan §4/§5): RTL's getByText matches a node's DIRECT child text
          only, so keeping these as two separate elements is what lets the label be queried without
          ever picking up the badge digit. */}
      <span className="tab-nav-item-label">{label}</span>
      {hasBadge && (
        <span className="tab-nav-item-badge" aria-hidden="true">
          {badgeText}
        </span>
      )}
    </button>
  );
};

export default TabBarNav;
